import { Modal, ModalSizes, Text, TextVariants, CommonComponentProps, LocalizationRes } from "@centricsoftware/design-system";
import { CANCEL_RES, OK_RES } from "csi/components/constants";
import { useLocale } from "designSystem/locale";
import React, { FunctionComponent, ReactNode } from "react";
import css from "./DialogComponent.module.css";
import ErrorIcon from "@mui/icons-material/Error";
import HelpIcon from "@mui/icons-material/Help";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { blue, green, red, yellow } from "designSystem/styles/color";

export type DialogCategory = "error" | "help" | "info" | "progress" | "success" | "warn";

export interface DialogComponentProps extends CommonComponentProps {
	/** localization resource for lone button if no buttons specified (defaults to OK) */
	buttonRes?: LocalizationRes;

	/** category of message which maps to default icon (default none) */
	category: DialogCategory;

	/** child components of message dialog (leaving possibility for extra content like checkboxes, etc.) */
	children?: {
		/** custom icon for the message dialog */
		icon?: ReactNode;
	};

	/** message to display in dialog (if not specifying localization resource) */
	message?: string;

	/** localization resource for confirmation message */
	messageRes?: string;

	/** parameters for localization resource for confirmation message */
	messageResParams?: Array<number | string>;

	/** on click handler for action button */
	onAction?: () => void;

	/** function to call when the dialog is closed */
	onClose: () => void;

	/** whether the dialog is open or not */
	open: boolean;

	/** title of the dialog */
	title?: string;

	/** localization resource for title */
	titleRes?: LocalizationRes;
}

const DialogComponent: FunctionComponent<DialogComponentProps> = (props) => {
	const {
		buttonRes,
		category,
		children,
		message,
		messageRes,
		onAction,
		onClose,
		open,
		title,
		titleRes,
		...restProps
	} = props;

	const { resource } = useLocale();
	let icon = children?.icon;

	const messageText = resource(messageRes) || message;

	if (!icon) {
		switch (category) {
			case "error":
				icon = <ErrorIcon style={{ color: red[500] }} />;
				break;
			case "help":
				icon = <HelpIcon style={{ color: blue[500] }} />;
				break;
			case "info":
				icon = <InfoIcon style={{ color: blue[500] }} />;
				break;
			case "warn":
				icon = <WarningIcon style={{ color: yellow[500] }} />;
				break;
			case "success":
				icon = <CheckCircleIcon style={{ color: green[600] }} />;
				break;
			default:
				icon = null;
		}
	}

	const messageComponent = (
		<div className={css.root}>
			{icon}
			<Text dataCsiAutomation="MessageText" variant={TextVariants.UiSemibold}>
				{messageText}
			</Text>
		</div>
	);

	return (
		<Modal
			actionButtonProps={{
				children: !buttonRes ? resource(OK_RES.res) : undefined,
				childrenRes: buttonRes,
				onClick: onAction || onClose,
			}}
			cancelButtonProps={{
				childrenRes: CANCEL_RES,
				onClick: onClose,
			}}
			dataCsiAutomation="DialogComponent"
			onClose={onClose}
			open={open}
			showActionButton
			showCancelButton
			showFooter
			size={ModalSizes.Medium}
			title={title}
			titleRes={titleRes}
			{...restProps}
		>
			<div className={css.content}>
				{messageComponent}
			</div>
		</Modal>
	);
};

export default DialogComponent;
